import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useApi, useQuery } from 'react-ui';
import { PartnerProfileService, type PartnerProfile, pendingAutoFit } from 'ts-core';

/**
 * Partner-scoped map mode. Active on `/partner/:code` (profile pane open) and
 * `/partner/:code/events` (pane closed). While active it:
 *  - injects a `providers:{shortName}` query layer so the map + list + search
 *    only show that partner's events (scoped server-side via /v3/search
 *    `providers=` = dataSourceProvider.shortName),
 *  - fetches the public profile (`/api/v2/partner-profiles/{code}`) for the
 *    header + pane.
 *
 * Mirrors UserScope, with two differences: the scope key (the partner's
 * data-source-provider short names) isn't in the URL — it comes from the
 * fetched profile — and the scope is only applied when the partner actually
 * HAS public upcoming events (`hasEvents`). Partners without events (any
 * partner can be a zeppelin/banner link target) get the detail pane over the
 * unchanged, generic events list.
 */
interface PartnerScopeValue {
    code: string | null;
    profile: PartnerProfile | null;
    loading: boolean;
    notFound: boolean;
    /** True once the profile resolved with events — the list/header scope to the partner. */
    hasEvents: boolean;
    paneOpen: boolean;
    openProfilePane: () => void;
    closeProfilePane: () => void;
}

const PartnerScopeContext = createContext<PartnerScopeValue>({
    code: null,
    profile: null,
    loading: false,
    notFound: false,
    hasEvents: false,
    paneOpen: false,
    openProfilePane: () => {},
    closeProfilePane: () => {},
});

export const usePartnerScope = (): PartnerScopeValue => useContext(PartnerScopeContext);

export const PartnerScopeProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const location = useLocation();
    const { apiEndpoints, apiKey } = useApi();
    const { addQuery, removeQueryById } = useQuery();

    const openMatch = matchPath('/partner/:code', location.pathname);
    const eventsMatch = matchPath('/partner/:code/events', location.pathname);
    const code = (openMatch?.params.code ?? eventsMatch?.params.code) ?? null;
    const isEventsPath = Boolean(eventsMatch);

    const service = useMemo(
        () => new PartnerProfileService(apiEndpoints, apiKey),
        [apiEndpoints, apiKey]
    );

    const [profile, setProfile] = useState<PartnerProfile | null>(null);
    const [loading, setLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);

    // Pane open/closed is real state (see UserScope for rationale). Lazily seeded
    // from the entry URL: open on `/partner/:code`, closed on `/partner/:code/events`.
    const [paneOpen, setPaneOpen] = useState<boolean>(() => Boolean(openMatch));
    const prevCodeRef = useRef<string | null>(code);
    const openProfilePane = useCallback(() => setPaneOpen(true), []);
    const closeProfilePane = useCallback(() => setPaneOpen(false), []);

    useEffect(() => {
        if (code !== prevCodeRef.current) {
            setPaneOpen(code ? !isEventsPath : false);
            prevCodeRef.current = code;
        }
    }, [code, isEventsPath]);

    // All of the partner's DSP short names (the backend events list spans every
    // DSP, so the map scope must too); singular field kept as fallback for older
    // API responses.
    const providerShortNames = useMemo(() => {
        const plural = profile?.dataSourceProviderShortNames ?? [];
        if (plural.length > 0) return plural;
        return profile?.dataSourceProviderShortName ? [profile.dataSourceProviderShortName] : [];
    }, [profile]);
    const providersKey = providerShortNames.join(',');

    // Whether this partner gets the FULL scoped experience (filtered list +
    // scoped header). Partners without public upcoming events only get the
    // detail pane — the events list stays generic/unfiltered.
    const hasEvents = (profile?.eventsCount ?? 0) > 0 && providerShortNames.length > 0;

    // Inject the partner scope query layer only once the profile has resolved
    // AND the partner has events. Until then the list is left untouched
    // (whatever the user was looking at stays), so there is no empty flash —
    // the list only changes when the scoped results arrive.
    useEffect(() => {
        if (!code || !hasEvents) return;
        const id = `partner:${code}`;
        // Arm one-shot map auto-fit so the map fits to this partner's events
        // (replaces the bespoke PartnerScopeMapFit fetch).
        pendingAutoFit.arm();
        addQuery({ id, providers: providerShortNames }, false);
        return () => {
            removeQueryById(id, false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code, hasEvents, providersKey]);

    // Fetch the public profile for the header + pane.
    useEffect(() => {
        let cancelled = false;
        if (!code) {
            setProfile(null);
            setNotFound(false);
            setLoading(false);
            return;
        }
        setLoading(true);
        setNotFound(false);
        service
            .getPartnerByCode(code)
            .then((p) => {
                if (cancelled) return;
                if (p) setProfile(p);
                else setNotFound(true);
            })
            .catch(() => {
                if (!cancelled) setNotFound(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [service, code]);

    const value = useMemo(
        () => ({
            code,
            profile,
            loading,
            notFound,
            hasEvents,
            paneOpen,
            openProfilePane,
            closeProfilePane,
        }),
        [code, profile, loading, notFound, hasEvents, paneOpen, openProfilePane, closeProfilePane]
    );

    return (
        <PartnerScopeContext.Provider value={value}>
            {children}
        </PartnerScopeContext.Provider>
    );
};
